import React, { useState, useCallback } from 'react';
import { Files, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DocumentUploader, DocumentList } from '@/components/documents';
import { documentsAPI } from '../../utils/api';

const DocumentsSection = ({ caseId, documents: initialDocuments, onDocumentAdded, isSubmitted = false }) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [documents, setDocuments] = useState(initialDocuments || []);
  const [recentlyCategorizedIds, setRecentlyCategorizedIds] = useState(new Set());

  // Sync with parent when initialDocuments changes
  React.useEffect(() => {
    setDocuments(initialDocuments || []);
  }, [initialDocuments]);

  // Called when a document is uploaded (still pending)
  const handleDocumentUploaded = useCallback((doc) => {
    setDocuments(prev => [doc, ...prev]);
  }, []);

  // Called when a document is categorized by AI
  const handleDocumentCategorized = useCallback((updatedDoc) => {
    setDocuments(prev =>
      prev.map(d => d.id === updatedDoc.id ? updatedDoc : d)
    );
    // Track as recently categorized for animation
    setRecentlyCategorizedIds(prev => new Set([...prev, updatedDoc.id]));
    // Clear from animation set after animation plays
    setTimeout(() => {
      setRecentlyCategorizedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(updatedDoc.id);
        return newSet;
      });
    }, 2500);
  }, []);

  // Called when all uploads and categorizations are complete
  const handleAllComplete = useCallback(() => {
    setUploadOpen(false);
    if (onDocumentAdded) {
      onDocumentAdded();
    }
  }, [onDocumentAdded]);

  // Handle document deletion
  const handleDeleteDocument = async (doc) => {
    await documentsAPI.deleteDocument(doc.id);
    setDocuments(prev => prev.filter(d => d.id !== doc.id));
    if (onDocumentAdded) {
      onDocumentAdded(); // Refresh parent data
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Files className="h-5 w-5 text-purple-500" />
          <h5 className="font-semibold">Documents</h5>
          <Badge variant="secondary" className="text-xs">{documents.length}</Badge>
        </div>
        <Collapsible open={uploadOpen} onOpenChange={setUploadOpen}>
          <CollapsibleTrigger asChild>
            <Button variant={uploadOpen ? 'default' : 'outline'} size="sm">
              <Upload className="h-3.5 w-3.5 mr-1" />
              {uploadOpen ? 'Close' : 'Upload'}
            </Button>
          </CollapsibleTrigger>
        </Collapsible>
      </div>

      <Collapsible open={uploadOpen} onOpenChange={setUploadOpen}>
        <CollapsibleContent>
          <div className="mb-4 p-4 bg-slate-50 rounded-lg">
            <DocumentUploader
              caseId={caseId}
              onDocumentUploaded={handleDocumentUploaded}
              onDocumentCategorized={handleDocumentCategorized}
              onAllComplete={handleAllComplete}
              showDropzone={true}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <DocumentList
        documents={documents}
        onDelete={handleDeleteDocument}
        showDelete={!isSubmitted}
        recentlyCategorizedIds={recentlyCategorizedIds}
        emptyMessage="No documents uploaded yet"
      />
    </div>
  );
};

export default DocumentsSection;
